import { useEffect, useMemo, useState } from "react";
import {
  defaultApi,
  isSessionExpiredError,
  SESSION_EXPIRED_MESSAGE,
  type DeliverySummary,
  type GroupSummary,
  type MeResponse
} from "../api/client";
import { closeTelegramWebApp } from "../telegram/webapp";
import { Composer } from "../features/composer/Composer";
import type { ComposerKind } from "../features/composer/model";
import { MoreScreen } from "../features/more/MoreScreen";
import { PostsScreen } from "../features/posts/PostsScreen";
import { PriceScreen } from "../features/price/PriceScreen";
import { deliveryActionFor } from "../features/delivery/DeliveryCenter";

type AppApi = { loadMe: () => Promise<MeResponse> } & Partial<Omit<typeof defaultApi, "loadMe">>;
type Section = "home" | "posts" | "price" | "more";

const navItems: Array<{ id: Section; label: string; icon: string }> = [
  { id: "home", label: "Главная", icon: "◆" },
  { id: "posts", label: "Посты", icon: "▤" },
  { id: "price", label: "Прайс", icon: "₽" },
  { id: "more", label: "Ещё", icon: "•••" }
];

const Skeleton = () => (
  <main className="app-shell skeleton-shell" aria-label="Загрузка прайс-центра">
    <div className="skeleton skeleton-title" />
    <div className="skeleton skeleton-rail" />
    <div className="skeleton skeleton-card" />
    <div className="skeleton skeleton-card short" />
  </main>
);

const ExpiredState = () => (
  <main className="fullscreen-state">
    <div className="state-mark" aria-hidden="true">↻</div>
    <p className="eyebrow">Telegram Mini App</p>
    <h1>Сессия истекла</h1>
    <p>Закройте это окно и откройте приложение заново из @THLS_AllPrice_bot.</p>
    <button className="primary-button" type="button" onClick={closeTelegramWebApp}>
      Закрыть
    </button>
  </main>
);

// Сессия может истечь, пока Mini App открыт: экраны сами такие ошибки часто глотают,
// поэтому замечаем их на уровне API и показываем общую плашку.
const watchSessionExpiry = <T extends Record<string, unknown>>(api: T, onExpired: () => void): T =>
  Object.fromEntries(Object.entries(api).map(([key, value]) => [
    key,
    typeof value !== "function"
      ? value
      : (...args: unknown[]) => {
          const result = (value as (...callArgs: unknown[]) => unknown)(...args);
          if (result && typeof (result as Promise<unknown>).then === "function") {
            return (result as Promise<unknown>).catch((error: unknown) => {
              if (isSessionExpiredError(error)) onExpired();
              throw error;
            });
          }
          return result;
        }
  ])) as T;

const SessionExpiredBanner = () => (
  <div className="session-banner" role="alert">
    <span>{SESSION_EXPIRED_MESSAGE}</span>
    <button type="button" onClick={closeTelegramWebApp}>Закрыть</button>
  </div>
);

const Home = ({ me, groups, deliveries, onCreate, onOpenHealth }: { me: MeResponse; groups: GroupSummary[]; deliveries: DeliverySummary[]; onCreate: (kind: ComposerKind) => void; onOpenHealth: () => void }) => {
  const unhealthyCount = groups.filter((group) => group.healthReasonCode).length;
  const deliveryIssueCount = deliveries.filter(
    (delivery) => deliveryActionFor(delivery, me.capabilities.admin) !== null
  ).length;
  const attentionCount = unhealthyCount + deliveryIssueCount;
  const countWord =
    attentionCount % 10 === 1 && attentionCount % 100 !== 11
      ? "событие требует"
      : [2, 3, 4].includes(attentionCount % 10) &&
          ![12, 13, 14].includes(attentionCount % 100)
        ? "события требуют"
        : "событий требуют";
  const unhealthyLabel = deliveryIssueCount > 0 && unhealthyCount === 0
    ? deliveryIssueCount % 10 === 1 && deliveryIssueCount % 100 !== 11
      ? `${deliveryIssueCount} публикация требует решения`
      : [2, 3, 4].includes(deliveryIssueCount % 10) &&
          ![12, 13, 14].includes(deliveryIssueCount % 100)
        ? `${deliveryIssueCount} публикации требуют решения`
        : `${deliveryIssueCount} публикаций требуют решения`
    : `${attentionCount} ${countWord} проверки`;
  return (
  <div className="screen-stack">
    <section className="hero-block">
      <div>
        <p className="eyebrow">Прайс-центр</p>
        <h1>Добрый день, {me.user.firstName}</h1>
        <p className="hero-copy">Подготовьте пост, проверьте цены и запустите публикацию.</p>
      </div>
      <span className="role-chip">{me.capabilities.admin ? "Админ" : "Клиент"}</span>
    </section>

    <section className="publish-rail" aria-label="Статус публикаций">
      <div className="rail-line" aria-hidden="true" />
      <article className="rail-stop current">
        <span className="rail-dot" />
        <div><span>Прайс THLS</span><strong>Готов к расчёту</strong></div>
        <time>Сейчас</time>
      </article>
      <article className="rail-stop">
        <span className="rail-dot" />
        <div><span>Ближайший выход</span><strong>Публикаций нет</strong></div>
        <time>—</time>
      </article>
    </section>

    <section className="action-grid" aria-label="Быстрые действия">
      <button className="action-card action-primary" type="button" onClick={() => onCreate("price")}>
        <span className="action-glyph">₽</span>
        <strong>Прайс-пост</strong>
        <small>С вашей наценкой</small>
      </button>
      <button className="action-card" type="button" onClick={() => onCreate("custom")}>
        <span className="action-glyph">Aa</span>
        <strong>Свой пост</strong>
        <small>Текст, медиа, кнопки</small>
      </button>
    </section>

    <button
      className={`attention-card ${attentionCount ? "has-alert" : ""}`}
      type="button"
      onClick={onOpenHealth}
    >
      <div>
        <p className="eyebrow">{attentionCount ? "Требует внимания" : "Состояние публикаций"}</p>
        <h2>{attentionCount ? unhealthyLabel : "Ошибок публикации нет"}</h2>
      </div>
      <span className={attentionCount ? "status-alert" : "status-ok"}>
        {attentionCount || "✓"}
      </span>
    </button>

    {me.capabilities.max && (
      <section className="admin-strip" aria-label="Администраторские возможности">
        <span>MAX</span>
        <p>Зеркало и глобальные настройки доступны только админу.</p>
      </section>
    )}
  </div>
  );
};

export function App({ api = defaultApi }: { api?: AppApi }) {
  const [sessionExpired, setSessionExpired] = useState(false);
  const resolvedApi = useMemo(
    () => watchSessionExpiry({ ...defaultApi, ...api }, () => setSessionExpired(true)),
    [api]
  );
  const deliveryApi = useMemo(() => ({
    loadDeliveries: resolvedApi.loadDeliveries,
    retryMaxDelivery: resolvedApi.retryMaxDelivery,
    markDeliveryPublished: resolvedApi.markDeliveryPublished,
    rescheduleDelivery: resolvedApi.rescheduleDelivery
  }), [resolvedApi]);
  const [me, setMe] = useState<MeResponse | null>(null);
  const [groups, setGroups] = useState<GroupSummary[]>([]);
  const [deliveries, setDeliveries] = useState<DeliverySummary[]>([]);
  const [section, setSection] = useState<Section>("home");
  const [postsInitialTab, setPostsInitialTab] = useState<"posts" | "deliveries">("posts");
  const [composerKind, setComposerKind] = useState<ComposerKind | null>(null);
  const [state, setState] = useState<"loading" | "ready" | "expired" | "error">("loading");
  const [online, setOnline] = useState(() => navigator.onLine);

  useEffect(() => {
    let active = true;
    resolvedApi.loadMe().then(
      (value) => {
        if (!active) return;
        setMe(value);
        setState("ready");
        void resolvedApi.loadGroups().then((items) => active && setGroups(items), () => undefined);
        void resolvedApi.loadDeliveries().then((items) => active && setDeliveries(items), () => undefined);
      },
      (error: { code?: string }) => {
        if (!active) return;
        setState(error?.code === "INIT_DATA_EXPIRED" ? "expired" : "error");
      }
    );
    return () => { active = false; };
  }, [resolvedApi]);

  useEffect(() => {
    const update = () => setOnline(navigator.onLine);
    window.addEventListener("online", update);
    window.addEventListener("offline", update);
    return () => { window.removeEventListener("online", update); window.removeEventListener("offline", update); };
  }, []);

  if (state === "loading") return <Skeleton />;
  if (state === "expired") return <ExpiredState />;
  if (state === "error" || !me) {
    return <main className="fullscreen-state"><div className="state-mark">!</div><h1>Не удалось открыть</h1><p>Проверьте связь и откройте Mini App из бота ещё раз.</p></main>;
  }

  if (composerKind) {
    return (
      <div className="app-shell composer-shell">
        {sessionExpired && <SessionExpiredBanner />}
        <Composer
          kind={composerKind}
          groups={groups}
          capabilities={me.capabilities}
          buttonLimits={me.limits.buttons}
          api={resolvedApi}
          onClose={() => setComposerKind(null)}
        />
      </div>
    );
  }

  return (
    <div className="app-shell">
      {sessionExpired && <SessionExpiredBanner />}
      {!online && <div className="offline-banner" role="status">Без сети: черновик сохранится, публикация недоступна.</div>}
      <main className="app-content">
        {section === "home" && <Home me={me} groups={groups} deliveries={deliveries} onCreate={setComposerKind} onOpenHealth={() => { if (groups.some((group) => group.healthReasonCode)) { setSection("more"); } else { setPostsInitialTab("deliveries"); setSection("posts"); } }} />}
        {section === "posts" && <PostsScreen initialTab={postsInitialTab} loadPosts={resolvedApi.loadPosts} capabilities={me.capabilities} buttonLimits={me.limits.buttons} deliveryApi={deliveryApi} editApi={{ loadPost: resolvedApi.loadPost, loadPostMedia: resolvedApi.loadPostMedia, uploadMedia: resolvedApi.uploadMedia, loadButtonSets: resolvedApi.loadButtonSets, loadPrice: resolvedApi.loadPrice, preview: resolvedApi.preview, updatePost: resolvedApi.updatePost, retryPostSync: resolvedApi.retryPostSync, replacePublishedPost: resolvedApi.replacePublishedPost, loadReplacementStatus: resolvedApi.loadReplacementStatus, deletePost: resolvedApi.deletePost }} onDeliveriesChange={setDeliveries} />}
        {section === "price" && <PriceScreen loadPrice={resolvedApi.loadPrice} />}
        {section === "more" && <MoreScreen capabilities={me.capabilities} buttonLimits={me.limits.buttons} loadGroups={resolvedApi.loadGroups} loadDrafts={resolvedApi.loadDrafts} loadTemplates={resolvedApi.loadTemplates} loadButtonSets={resolvedApi.loadButtonSets} buttonSetsApi={{ loadButtonSets: resolvedApi.loadButtonSets, parseButtonSet: resolvedApi.parseButtonSet, createButtonSet: resolvedApi.createButtonSet, updateButtonSet: resolvedApi.updateButtonSet, copyButtonSet: resolvedApi.copyButtonSet, deleteButtonSet: resolvedApi.deleteButtonSet }} categorySyncApi={{ loadCategorySync: resolvedApi.loadCategorySync, configureCategorySync: resolvedApi.configureCategorySync, runCategorySync: resolvedApi.runCategorySync }} checkGroup={resolvedApi.checkGroup} enableGroup={resolvedApi.enableGroup} onOpenDeliveries={() => { setPostsInitialTab("deliveries"); setSection("posts"); }} />}
      </main>
      <nav className="bottom-nav" aria-label="Основная">
        {navItems.map((item) => (
          <button key={item.id} type="button" className={section === item.id ? "active" : ""} aria-current={section === item.id ? "page" : undefined} onClick={() => { if (item.id === "posts") setPostsInitialTab("posts"); setSection(item.id); }}>
            <span aria-hidden="true">{item.icon}</span><small>{item.label}</small>
          </button>
        ))}
      </nav>
    </div>
  );
}
